import AssignmentIcon from '@mui/icons-material/Assignment';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import CompositeDialog from '../dialog/compositeDialog';
import { RichTextEditorRef } from './rich-text-editor';

import {
  CreateNoteForm,
  CreateNoteFormSchema,
  NoteDto,
  UpdateNoteForm,
  UpdateNoteFormSchema,
  UserDto,
  UserNeedDto,
} from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { DialogFooterAction } from 'react-components/dialog/dialog-footer';
import { TrackingProvider } from 'react-components/tracking';
import { autoSaveNotesRequests } from 'redux/requests/auto-save.requests';
import { notesRequests } from 'redux/requests/notes.request';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { createNoteSuccess, updateNoteSuccess } from 'redux/slices/notesSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { useToast } from '../app/ReusableToast';
import Loader from '../workflows/loader';
import useConfirmationAlertDialog from './hooks/use-confirmation-alert-dialog';
import useUnsavedChangesDialog from './hooks/use-unsaved-changes-dialog';

export interface NoteDialogInterfaceProps {
  user: UserDto;
  note?: NoteDto;
  need?: UserNeedDto;
}

interface NoteDialogProps extends NoteDialogInterfaceProps {
  handleClose: () => void;
  mode: 'Create' | 'Edit';
  open: boolean;
}

const NoteDialog = ({
  handleClose,
  mode,
  note,
  open,
  user,
}: NoteDialogProps) => {
  const schema =
    mode === 'Create' ? CreateNoteFormSchema : UpdateNoteFormSchema;
  const { control, handleSubmit, reset, formState } = useForm<
    CreateNoteForm | UpdateNoteForm
  >({
    mode: 'onSubmit',
    defaultValues: {
      text: mode === 'Edit' ? note?.text : '<p><br></p>',
      userId: user.id,
    },
    resolver: zodResolver(schema),
  });
  const dispatch = useAppDispatch();
  const toast = useToast();
  const openConfirmationAlertDialog = useConfirmationAlertDialog();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const canDelete = !!note?.id && note?.createdBy === actingUser.id;

  // Ref to access RichTextEditor methods
  const richTextEditorRef = useRef<RichTextEditorRef>(null);

  const [updatedNote, isUpdatingNote, updateNote] = useApiCall(
    notesRequests.updateNote
  );
  const [_deletedAutoSave, _isAutoSaveDeleting, deleteAutoSave] = useApiCall(
    autoSaveNotesRequests.deleteAutoSavedNote
  );
  const [autoSavedNote, isLoadingGet, getAutoSavedNote] = useApiCall(
    autoSaveNotesRequests.getAutoSavedNote
  );
  const [_autoSaveAction, _isLoadingSave, postAutoSavedNote] = useApiCall(
    autoSaveNotesRequests.postAutoSavedNote
  );
  const [_deleteResponse, isDeleting, deleteNote] = useApiCall(
    notesRequests.deleteNote
  );

  const [createdNote, isLoadingCreate, createNote] = useApiCall(
    notesRequests.createNotes
  );
  const [manualSave, setManualSave] = useState(false);

  const isSubmitting = isLoadingCreate || isUpdatingNote || isDeleting;

  const formConfig: FormConfig<CreateNoteForm | UpdateNoteForm> = [
    {
      label: 'Note',
      type: 'input',
      input: 'richText',
      name: 'text',
      props: {
        autoSaveFn: handleAutoSave,
        showCharacterCount: true,
        trackingName: 'note-text-input',
      },
    },
  ];

  const { isDirty } = formState;
  const onSubmit = async (data: CreateNoteForm | UpdateNoteForm) => {
    if (mode === 'Edit' && !isDirty) {
      handleClose();
      return;
    }

    try {
      // Cancel any pending autosave before proceeding with manual save
      if (richTextEditorRef.current) {
        richTextEditorRef.current.cancelPendingAutoSave();
      }

      // If we cancelled an autosave, the form data might not be up to date
      // Get the current value from the RichTextEditor to ensure we have the latest
      if (richTextEditorRef.current) {
        const currentNoteValue = richTextEditorRef.current.getCurrentValue();
        data.text = currentNoteValue;
      }

      if (note && mode === 'Edit') {
        setManualSave(true);
        updateNote({
          params: { noteId: note?.id },
          body: { noteId: note?.id, text: data.text },
        });
      } else {
        createNote({
          body: {
            userId: user.id,
            text: data.text,
          },
        });
        deleteAutoSave({
          params: { subjectId: user?.id, type: 'notes' },
        });
      }

      // Mark the current value as saved to prevent unnecessary autosaves
      if (richTextEditorRef.current) {
        richTextEditorRef.current.markAsSaved(data.text);
      }
    } catch (err) {
      console.error(`Error saving note: ${err}`);
    }
  };

  useEffect(() => {
    if (createdNote) {
      dispatch(createNoteSuccess(createdNote));
      toast('Note created successfully');
      reset();
      handleClose();
    }
    if (updatedNote && manualSave) {
      dispatch(updateNoteSuccess(updatedNote));
      toast('Note updated successfully');
      reset();
      handleClose();
    } else if (updatedNote && !manualSave) {
      setManualSave(false);
    }
  }, [createdNote, updatedNote]);

  async function handleDelete() {
    const confirmDelete = await openConfirmationAlertDialog({
      title: 'Delete note',
      icon: <ErrorOutlineIcon />,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      avatarCaption: 'Are you sure?',
      message: 'This note will no longer be available',
      confirmationText: 'Yes, delete now',
      confirmButtonColor: 'error',
    });
    if (confirmDelete) {
      deleteNote({ params: { noteId: note?.id } });
      reset();
      toast('Note deleted successfully');
      handleClose();
    }
  }

  useEffect(() => {
    if (mode === 'Edit') return;

    async function getAutoSaved() {
      try {
        getAutoSavedNote({
          params: { subjectId: user?.id, type: 'notes' },
        });
      } catch (err) {
        console.error(`Error getting autosaved note: ${err}`);
      }
    }

    void getAutoSaved();
  }, []);

  useEffect(() => {
    if (!autoSavedNote || mode === 'Edit') return;
    reset({
      text: autoSavedNote.text,
      userId: user.id,
    });
  }, [autoSavedNote, mode, reset, user.id]);

  async function handleAutoSave(autoSaveNote: string) {
    if (mode === 'Create') {
      postAutoSavedNote({
        params: { subjectId: user?.id, type: 'notes' },
        body: { text: autoSaveNote },
      });
    } else {
      updateNote({
        params: { noteId: note?.id },
        body: { noteId: note?.id, text: autoSaveNote },
      });
    }
  }

  const actions: DialogFooterAction[] = [
    {
      text: 'Cancel',
      callback: () => handleFormClose(),
      variant: 'outlined',
      disabled: isSubmitting,
    },
    {
      text: 'Save',
      type: 'submit',
      variant: 'contained',
      disabled: isSubmitting,
    },
  ];

  if (canDelete) {
    actions.unshift({
      text: 'Delete',
      isLeft: true,
      callback: handleDelete,
      disabled: isSubmitting,
    });
  }

  const handleFormClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        deleteAutoSave({
          params: { subjectId: user?.id, type: 'notes' },
        });
        handleClose();
      });
    } else {
      handleClose();
    }
  };

  return (
    <TrackingProvider contextName="note-dialog">
      <CompositeDialog
        handleClose={handleFormClose}
        maxWidth="md"
        open={open}
        icon={<AssignmentIcon color="primary" sx={{ marginRight: 1 }} />}
        title={`${mode === 'Create' ? 'New' : mode} Note for ${
          user?.firstName
        } ${user?.lastName}`}
        handleFormSubmit={handleSubmit(onSubmit)}
        actions={actions}
      >
        {mode === 'Create' && isLoadingGet && <Loader />}
        {(mode === 'Edit' || (mode === 'Create' && !isLoadingGet)) && (
          <React.Fragment>
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
            />
          </React.Fragment>
        )}
      </CompositeDialog>
    </TrackingProvider>
  );
};

export default NoteDialog;
